<template>
    <div
        class="activix-datepicker relative inline-block"
        :class="{
            'activix-datepicker--range': isRange,
            'activix-datepicker--datetime': enableTimePicker,
            'activix-datepicker--with-presets': hasPresets,
        }"
    >
        <vue-date-picker
            :model-value="modelValue"
            :enable-time-picker="enableTimePicker"
            :range="isRange"
            :locale="locale"
            :multi-calendars="calendarsConfig"
            :min-date="minDate"
            :max-date="maxDate"
            :start-date="startDate"
            :flow="flowConfig"
            :disabled="disabled"
            :clearable="clearable"
            :auto-apply="autoApply"
            :config="vendorConfig"
            :time-config="timeConfig"
            :floating="floatingConfig"
            :ui="uiConfig"
            ref="datepickerRef"
            @date-click="onDateClick"
            @flow-step="onFlowStep"
            @open="onOpen"
            @update:model-value="onUpdate"
        >
            <template #trigger>
                <slot name="trigger">
                    <div
                        data-testid="datepicker-trigger"
                        class="flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 px-3 py-2"
                        :class="{ 'cursor-not-allowed opacity-50': disabled }"
                    >
                        <activix-icon
                            data-testid="datepicker-trigger-calendar-icon"
                            name="regular/calendar-3"
                            class="size-4 text-gray-500"
                        />
                        <span
                            data-testid="datepicker-trigger-text"
                            class="text-sm"
                            :class="modelValue ? 'text-gray-900' : 'text-gray-400'"
                            v-if="!compact"
                        >
                            {{ triggerText }}
                        </span>
                    </div>
                </slot>
            </template>

            <template #calendar-icon>
                <activix-icon
                    data-testid="datepicker-calendar-icon"
                    name="regular/calendar-3"
                    class="size-4 text-gray-500"
                />
            </template>

            <template #clock-icon>
                <activix-icon
                    data-testid="datepicker-clock-icon"
                    name="regular/time-clock-circle"
                    class="size-4 text-gray-500"
                />
            </template>

            <template #arrow-left>
                <activix-icon
                    data-testid="datepicker-arrow-left-icon"
                    class="size-4"
                    name="regular/arrow-left-1"
                />
            </template>

            <template #arrow-right>
                <activix-icon
                    data-testid="datepicker-arrow-right-icon"
                    class="size-4"
                    name="regular/arrow-right-1"
                />
            </template>

            <template v-if="isRange" #menu-header>
                <div
                    data-testid="datepicker-range-header"
                    class="activix-datepicker__range-header grid grid-cols-2 gap-7 px-4 pb-2 pt-3 text-center text-base font-semibold text-gray-800"
                >
                    <div
                        class="activix-datepicker__range-label activix-datepicker__range-label--start"
                        data-testid="datepicker-start-label"
                    >
                        {{ startLabel }}
                    </div>
                    <div
                        class="activix-datepicker__range-label activix-datepicker__range-label--end"
                        data-testid="datepicker-end-label"
                    >
                        {{ endLabel }}
                    </div>
                </div>
            </template>

            <template #time-picker-overlay="{ hours, minutes, setHours, setMinutes, setSeconds }">
                <date-picker-time-overlay
                    :hours="firstTimeValue(hours)"
                    :minutes="firstTimeValue(minutes)"
                    :time-slots="timeSlots"
                    :time-slot-grid="timeSlotGrid"
                    :uses24-hour-time="uses24HourTime"
                    :selected-date-text="selectedTimePickerDateText"
                    :previous-day-label="previousDayLabel"
                    :next-day-label="nextDayLabel"
                    :can-shift-previous="canShiftSelectedDate(-1)"
                    :can-shift-next="canShiftSelectedDate(1)"
                    @select-slot="selectTimeSlot($event, setHours, setMinutes, setSeconds)"
                    @adjust-hours="adjustHours(hours, minutes, setHours, $event)"
                    @adjust-minutes="adjustMinutes(hours, minutes, setMinutes, setSeconds, $event)"
                    @toggle-period="togglePeriod(hours, minutes, setHours)"
                    @shift-date="shiftSelectedDate($event)"
                />
            </template>

            <template v-if="hasRangePresets" #right-sidebar>
                <div
                    data-testid="datepicker-preset-list"
                    class="activix-datepicker__preset-list activix-datepicker__preset-list--with-action-clearance relative flex min-w-40 flex-col overflow-hidden"
                >
                    <div
                        class="preset-scroll preset-scroll--minimal-scrollbar flex grow flex-col gap-1.5 overflow-y-auto px-2 py-1"
                    >
                        <button
                            type="button"
                            data-testid="datepicker-preset"
                            class="activix-datepicker__preset-button whitespace-nowrap rounded px-3.5 py-1.5 text-left text-sm transition-colors"
                            :class="
                                isPresetSelected(preset)
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-blue-500 hover:text-white'
                            "
                            :data-selected="isPresetSelected(preset)"
                            :aria-pressed="isPresetSelected(preset)"
                            :key="preset.label"
                            @click="selectPreset(preset)"
                            v-for="preset in presetDates"
                        >
                            {{ preset.label }}
                        </button>
                    </div>
                    <div
                        class="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent"
                    />
                </div>
            </template>

            <template #action-row="{ selectDate, closePicker }">
                <div
                    data-testid="datepicker-action-row"
                    class="activix-datepicker__action-row flex w-full flex-col gap-3"
                >
                    <div
                        data-testid="datepicker-single-preset-list"
                        class="activix-datepicker__single-preset-list flex flex-col flex-wrap gap-1.5 items-stretch justify-start"
                        v-if="hasSinglePresets"
                    >
                        <button
                            type="button"
                            data-testid="datepicker-single-preset"
                            class="activix-datepicker__preset-button whitespace-nowrap rounded px-3.5 py-1.5 text-left text-sm transition-colors hover:bg-blue-500 hover:text-white text-center"
                            :class="
                                isPresetSelected(preset)
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-700'
                            "
                            :data-selected="isPresetSelected(preset)"
                            :aria-pressed="isPresetSelected(preset)"
                            :key="preset.label"
                            @click="selectPreset(preset)"
                            v-for="preset in presetDates"
                        >
                            {{ preset.label }}
                        </button>
                    </div>

                    <!-- Top row: the calendar/time view switch, spanning the full width. -->
                    <div
                        data-testid="datepicker-action-switch-row"
                        class="activix-datepicker__action-switch-row w-full"
                        v-if="enableTimePicker"
                    >
                        <button
                            type="button"
                            data-testid="datepicker-view-switch-button"
                            class="activix-datepicker__action-button activix-datepicker__view-switch-button flex w-full min-w-0 items-center justify-center gap-1 rounded bg-white px-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                            @click="switchDateTimeView()"
                        >
                            <activix-icon
                                data-testid="datepicker-switcher-calendar-icon"
                                name="regular/calendar-3"
                                class="size-4 shrink-0 text-gray-500"
                                v-if="isTimePickerActive"
                            />
                            <template v-else>
                                <activix-icon
                                    data-testid="datepicker-switcher-clock-icon"
                                    name="regular/time-clock-circle"
                                    class="size-4 shrink-0 text-gray-500"
                                />
                                <span class="min-w-0 truncate" v-if="selectedTimeSwitcherText">
                                    {{ selectedTimeSwitcherText }}
                                </span>
                            </template>
                        </button>
                    </div>

                    <!-- Bottom row: clear on the left, cancel + submit grouped on the right. -->
                    <div
                        data-testid="datepicker-action-buttons"
                        class="flex w-full items-center justify-between gap-3"
                        v-if="(clearable && hasValue) || actionButtonsVisible"
                    >
                        <button
                            type="button"
                            data-testid="datepicker-clear-button"
                            class="activix-datepicker__clear-button text-sm font-medium text-blue-500 transition-colors hover:text-blue-600 ml-2"
                            @click="clearValue"
                            v-if="clearable && hasValue"
                        >
                            {{ clearLabel }}
                        </button>
                        <div class="ml-auto flex items-center gap-3" v-if="actionButtonsVisible">
                            <button
                                type="button"
                                data-testid="datepicker-cancel-button"
                                class="activix-datepicker__action-button activix-datepicker__cancel-button rounded border border-gray-300 bg-white text-sm font-medium text-gray-700 shadow-md transition-colors hover:bg-gray-100"
                                :class="isRange ? 'px-3' : 'px-4'"
                                @click="closePicker"
                            >
                                {{ cancelLabel }}
                            </button>
                            <button
                                type="button"
                                data-testid="datepicker-submit-button"
                                class="activix-datepicker__action-button activix-datepicker__submit-button rounded bg-blue-500 text-sm font-medium text-white shadow-md transition-colors hover:bg-blue-600"
                                :class="isRange ? 'px-3' : 'px-4'"
                                @click="selectDate"
                            >
                                {{ submitLabel }}
                            </button>
                        </div>
                    </div>
                </div>
            </template>
        </vue-date-picker>
    </div>
</template>

<script setup lang="ts">
    import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
    import { format as fnsFormat, startOfDay } from 'date-fns';
    import { VueDatePicker } from '@vuepic/vue-datepicker';
    import ActivixIcon from '@/components/elements/ActivixIcon.vue';
    import DatePickerTimeOverlay from '@/components/activix-ui/ActivixDatePicker/DatePickerTimeOverlay.vue';
    import { DEFAULT_TIME_SLOT_INTERVAL_MINUTES } from '@/components/activix-ui/ActivixDatePicker/constants.ts';
    import '@vuepic/vue-datepicker/dist/main.css';
    import type { Locale } from 'date-fns';
    import type { PickerSection } from '@vuepic/vue-datepicker';
    import type { TimeSlot } from '@/components/activix-ui/ActivixDatePicker/constants.ts';

    const MINUTES_PER_DAY = 24 * 60;

    export type TimeFormat = 'locale' | '12h' | '24h';

    export type DatePickerValue = Date | Date[] | null;

    /**
     * Accepted shapes for the `multiCalendars` prop. Mirrors the subset of the
     * vendor's `multi-calendars` config this component forwards: a boolean or
     * number toggle, or the `{ solo, count }` object the range layout builds.
     */
    export type MultiCalendarsConfig = boolean | number | { solo?: boolean; count?: number };

    // The primitive's preset contract: a resolved display string and its value.
    // The app-facing ActivixDatePicker exposes the richer, i18n-aware DatePreset
    // and resolves it down to this shape; this type is internal to the primitive.
    export interface RawDatePreset {
        label: string;
        value: Date | [Date, Date];
    }

    export interface RawDatePickerProps {
        modelValue?: DatePickerValue;
        mode?: 'date' | 'datetime' | 'range';
        locale: Locale;
        minDate?: Date;
        maxDate?: Date;
        startDate?: Date;
        selectionFlow?: PickerSection[];
        disabled?: boolean;
        clearable?: boolean;
        placeholder?: string;
        multiCalendars?: MultiCalendarsConfig;
        presetDates?: RawDatePreset[];
        timeSlotIntervalMinutes?: number;
        timeSlotGrid?: boolean;
        timeFormat?: TimeFormat;
        // date-fns pattern for the trigger/calendar-header date. The bare picker
        // stays locale-agnostic by default; wrappers inject a locale-aware token
        // (e.g. `PP`) so the date order follows the user's language.
        dateFormat?: string;
        showActionButtons?: boolean;
        initiallyOpen?: boolean;
        compact?: boolean;
        submitLabel?: string;
        cancelLabel?: string;
        clearLabel?: string;
        startLabel?: string;
        endLabel?: string;
        previousDayLabel?: string;
        nextDayLabel?: string;
    }

    const props = withDefaults(defineProps<RawDatePickerProps>(), {
        modelValue: null,
        mode: 'date',
        minDate: undefined,
        maxDate: undefined,
        startDate: undefined,
        selectionFlow: undefined,
        disabled: false,
        clearable: false,
        placeholder: '',
        multiCalendars: undefined,
        presetDates: undefined,
        timeSlotIntervalMinutes: DEFAULT_TIME_SLOT_INTERVAL_MINUTES,
        timeSlotGrid: false,
        timeFormat: 'locale',
        dateFormat: 'MMM dd, yyyy',
        showActionButtons: true,
        initiallyOpen: false,
        compact: false,
        submitLabel: 'Submit',
        cancelLabel: 'Cancel',
        clearLabel: 'Clear',
        startLabel: 'Start date',
        endLabel: 'End date',
        previousDayLabel: 'Previous day',
        nextDayLabel: 'Next day',
    });

    const emit = defineEmits<{
        'update:modelValue': [value: DatePickerValue];
    }>();

    const activePickerSection = ref<PickerSection>('calendar');
    const pendingSelectedSingleDate = ref<Date | null>(null);
    // The vendor keeps time edits in its own pending state and only flushes them
    // to the model on submit, so the time chosen in the overlay is not reflected
    // back on the model until then. We mirror those edits here so the calendar
    // view (e.g. the switcher button) can show the in-progress time. Reset
    // whenever the menu opens or the committed value changes.
    const pendingTime = ref<{ hours: number; minutes: number } | null>(null);
    const datepickerRef = useTemplateRef<{
        openMenu: () => void;
        closeMenu: () => void;
        selectDate: () => void;
        switchView: (view: 'month' | 'year' | 'calendar' | 'time', instance?: number) => void;
        updateInternalModelValue: (value: Date | Date[] | null) => void;
    }>('datepickerRef');
    const floatingConfig = { arrow: true };
    const uiConfig = { menu: 'activix-datepicker__menu' };
    const flowConfig = computed(() =>
        props.selectionFlow?.length ? { steps: props.selectionFlow } : undefined
    );

    const enableTimePicker = computed(() => props.mode === 'datetime');
    const isRange = computed(() => props.mode === 'range');
    // Without a Submit button there is no explicit commit. Date mode uses the
    // vendor's auto-apply: a pick commits and closes. Datetime keeps our action
    // row (auto-apply would remove it, taking the calendar/time view switch with
    // it), so instead we commit on outside click — see onClickOutside.
    // Range selection always needs an explicit Submit/Cancel: a range has no
    // single "pick" that can auto-commit, so showActionButtons is forced on and
    // the prop only applies to date and datetime modes.
    const actionButtonsVisible = computed(() => isRange.value || props.showActionButtons);
    const autoApply = computed(() => !actionButtonsVisible.value && !enableTimePicker.value);
    const commitOnClose = computed(() => !actionButtonsVisible.value && enableTimePicker.value);
    // onClickOutside lives in the vendor's `config` object, not as a top-level
    // prop — passing it any other way is silently ignored.
    const vendorConfig = computed(() => ({ onClickOutside }));
    const hasRangePresets = computed(() => isRange.value && Boolean(props.presetDates?.length));
    const hasSinglePresets = computed(() => !isRange.value && Boolean(props.presetDates?.length));
    const hasPresets = computed(() => Boolean(props.presetDates?.length));
    const hasValue = computed(() => {
        if (Array.isArray(props.modelValue)) {
            const [start, end] = props.modelValue;
            return Boolean(start && end);
        }

        return props.modelValue != null;
    });
    const normalizedTimeSlotIntervalMinutes = computed(() => {
        const interval = Math.trunc(props.timeSlotIntervalMinutes);

        if (!Number.isFinite(interval) || interval < 1) {
            return DEFAULT_TIME_SLOT_INTERVAL_MINUTES;
        }

        return Math.min(interval, 60);
    });
    const uses24HourTime = computed(() => {
        if (props.timeFormat === '24h') return true;
        if (props.timeFormat === '12h') return false;

        return uses24HourLocale(props.locale);
    });
    const timeFormatPattern = computed(() => {
        if (props.timeFormat === 'locale') {
            return 'p';
        }

        return uses24HourTime.value ? 'HH:mm' : 'h:mm a';
    });
    const timeConfig = computed(() => ({
        is24: uses24HourTime.value,
        minutesIncrement: normalizedTimeSlotIntervalMinutes.value,
        minutesGridIncrement: normalizedTimeSlotIntervalMinutes.value,
    }));
    const calendarsConfig = computed(() => {
        if (props.multiCalendars !== undefined) return props.multiCalendars;
        if (props.mode === 'range') {
            return { solo: true, count: 2 };
        }
        return undefined;
    });
    const selectedSingleDate = computed(() => {
        if (!props.modelValue || Array.isArray(props.modelValue)) {
            return null;
        }

        return Number.isNaN(props.modelValue.getTime()) ? null : props.modelValue;
    });
    const timePickerSelectedDate = computed(
        () => pendingSelectedSingleDate.value ?? selectedSingleDate.value
    );
    const selectedTimePickerDateText = computed(() => {
        if (!enableTimePicker.value || activePickerSection.value !== 'time') return '';
        if (!timePickerSelectedDate.value) return '';

        return formatCalendarDate(timePickerSelectedDate.value);
    });
    const selectedTimeSwitcherText = computed(() => {
        if (!enableTimePicker.value) return '';

        // Prefer the in-progress time picked in the overlay (not yet committed to
        // the model) over the committed value, so switching back to the calendar
        // shows what the user just chose.
        if (pendingTime.value) {
            const base = timePickerSelectedDate.value ?? new Date();
            const withPendingTime = new Date(base);
            withPendingTime.setHours(pendingTime.value.hours, pendingTime.value.minutes, 0, 0);

            return formatTime(withPendingTime);
        }

        if (!selectedSingleDate.value) return '';

        return formatTime(selectedSingleDate.value);
    });
    const isTimePickerActive = computed(() => activePickerSection.value === 'time');
    const timeSlots = computed<TimeSlot[]>(() => {
        if (!props.timeSlotGrid) return [];

        const slots: TimeSlot[] = [];

        for (
            let totalMinutes = 0;
            totalMinutes < MINUTES_PER_DAY;
            totalMinutes += normalizedTimeSlotIntervalMinutes.value
        ) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            slots.push({
                label: fnsFormat(new Date(2000, 0, 1, hours, minutes), timeFormatPattern.value, {
                    locale: props.locale,
                }),
                value: `${hours}:${minutes.toString().padStart(2, '0')}`,
                hours,
                minutes,
            });
        }

        return slots;
    });

    const triggerText = computed(() => {
        if (!props.modelValue) {
            return props.placeholder;
        }

        if (Array.isArray(props.modelValue)) {
            const [start, end] = props.modelValue;
            if (!start || !end) return props.placeholder;
            return `${formatTriggerValue(start)} - ${formatTriggerValue(end)}`;
        }

        return formatTriggerValue(props.modelValue);
    });

    watch(
        () => props.modelValue,
        () => {
            pendingSelectedSingleDate.value = null;
            pendingTime.value = null;
        }
    );

    onMounted(() => {
        if (props.initiallyOpen && !props.disabled) {
            // The vendor exposes openMenu asynchronously, so guard the method
            // itself rather than only the ref: optional chaining still throws
            // when the ref is present but openMenu has not been exposed yet.
            nextTick(() => {
                if (typeof datepickerRef.value?.openMenu === 'function') {
                    datepickerRef.value.openMenu();
                }
            });
        }
    });

    function formatTriggerValue(date: Date): string {
        if (props.mode === 'datetime') {
            return `${formatCalendarDate(date)} ${formatTime(date)}`;
        }
        return formatCalendarDate(date);
    }

    function formatCalendarDate(date: Date): string {
        return fnsFormat(date, props.dateFormat, { locale: props.locale });
    }

    function formatTime(date: Date): string {
        return fnsFormat(date, timeFormatPattern.value, { locale: props.locale });
    }

    function uses24HourLocale(locale: Locale): boolean {
        if (!locale.code) {
            return false;
        }

        try {
            return !new Intl.DateTimeFormat(locale.code, { hour: 'numeric' })
                .formatToParts(new Date(2000, 0, 1, 13))
                .some((part) => part.type === 'dayPeriod');
        } catch {
            return false;
        }
    }

    function onUpdate(value: DatePickerValue) {
        pendingSelectedSingleDate.value = null;
        emit('update:modelValue', value);
    }

    // Replaces the vendor's default outside-click handling. In datetime mode
    // without action buttons there is no Submit, so an outside click commits the
    // pending selection the same way the Submit button does (selectDate also
    // closes the menu). Every other configuration just closes, discarding any
    // uncommitted edits exactly as before.
    function onClickOutside() {
        if (commitOnClose.value) {
            datepickerRef.value?.selectDate();
        } else {
            datepickerRef.value?.closeMenu();
        }
    }

    function onOpen() {
        activePickerSection.value = 'calendar';
        pendingSelectedSingleDate.value = null;
        pendingTime.value = null;

        if (enableTimePicker.value) {
            resetToCalendarView();
        }
    }

    function onFlowStep(step: PickerSection) {
        activePickerSection.value = step;
    }

    function onDateClick() {
        if (!enableTimePicker.value || isRange.value) {
            return;
        }

        nextTick(() => {
            switchDateTimeView('time');
        });
    }

    function switchDateTimeView(section?: 'calendar' | 'time') {
        const nextSection = section ?? (isTimePickerActive.value ? 'calendar' : 'time');
        activePickerSection.value = nextSection;
        // The vendor's public switchView opens or closes the time-picker overlay
        // directly: 'time' shows it, 'calendar' hides it.
        datepickerRef.value?.switchView(nextSection);
    }

    // Force the menu back to the calendar view, closing the time-picker overlay
    // if it is open. The vendor keeps its view state across close/reopen, so
    // submitting from the time view leaves the menu showing the time overlay on
    // the next open — which covers the calendar and swallows its pointer events.
    // `switchView('calendar')` resets that through the vendor's public API
    // (no-op when already on the calendar). Two ticks so the menu has mounted
    // and the vendor's calendar ref is ready before we call it.
    function resetToCalendarView() {
        nextTick(() => {
            datepickerRef.value?.switchView('calendar');
        });
    }

    // Match on the calendar day unless the time itself is part of the value
    // (datetime mode). A wrapper may route the value through a normalization
    // step that shifts a date-only value off midnight — ActivixDate, for
    // example, parks date-only values at midday so a timezone conversion cannot
    // bump the calendar day — while presets are seeded at the start of the day.
    // An exact-instant comparison would then never match, so date and range
    // presets compare by day; datetime presets keep the full instant.
    function datesEqual(a: Date | null | undefined, b: Date | null | undefined): boolean {
        if (!(a instanceof Date) || !(b instanceof Date)) {
            return false;
        }

        if (enableTimePicker.value) {
            return a.getTime() === b.getTime();
        }

        return startOfDay(a).getTime() === startOfDay(b).getTime();
    }

    // Derive the preset highlight from the committed value rather than tracking
    // the last clicked label: the highlight then self-corrects when the value
    // changes by any path (manual pick, external update), and presets are matched
    // by value identity instead of a translated label that could collide.
    function isPresetSelected(preset: RawDatePreset): boolean {
        const value = props.modelValue;

        if (Array.isArray(preset.value)) {
            return (
                Array.isArray(value) &&
                datesEqual(value[0], preset.value[0]) &&
                datesEqual(value[1], preset.value[1])
            );
        }

        return !Array.isArray(value) && datesEqual(value, preset.value);
    }

    function selectPreset(preset: RawDatePreset) {
        pendingSelectedSingleDate.value = null;
        emit('update:modelValue', preset.value);
        datepickerRef.value?.closeMenu();
    }

    function clearValue() {
        pendingSelectedSingleDate.value = null;
        emit('update:modelValue', null);
        datepickerRef.value?.closeMenu();
    }

    function getShiftedSelectedDate(dayOffset: number): Date | null {
        if (!timePickerSelectedDate.value) {
            return null;
        }

        const shiftedDate = new Date(timePickerSelectedDate.value);
        shiftedDate.setDate(shiftedDate.getDate() + dayOffset);
        // Carry the uncommitted overlay time onto the shifted day so the date
        // pushed to the vendor matches the time the switcher shows. Without this
        // the shifted Date keeps the committed time while pendingTime drives the
        // switcher, and the two drift apart.
        if (pendingTime.value) {
            shiftedDate.setHours(pendingTime.value.hours, pendingTime.value.minutes, 0, 0);
        }

        return isDateWithinBounds(shiftedDate) ? shiftedDate : null;
    }

    // Bounds are compared as full instants, not calendar days: a date-only
    // minDate at midnight rejects the same day before its midnight, and a
    // selected datetime at 12:00 sits inside it. Callers that want a whole-day
    // bound should pass the day's start/end accordingly.
    function isDateWithinBounds(date: Date): boolean {
        if (props.minDate && date < props.minDate) return false;
        if (props.maxDate && date > props.maxDate) return false;

        return true;
    }

    function canShiftSelectedDate(dayOffset: number): boolean {
        return Boolean(getShiftedSelectedDate(dayOffset));
    }

    function shiftSelectedDate(dayOffset: number) {
        const shiftedDate = getShiftedSelectedDate(dayOffset);

        if (!shiftedDate) {
            return;
        }

        pendingSelectedSingleDate.value = shiftedDate;
        datepickerRef.value?.updateInternalModelValue(shiftedDate);
    }

    // The vendor reports time as a scalar in single mode and a tuple in range
    // mode; we only drive the single-date time, so take the first value.
    function firstTimeValue(value: number | number[]): number {
        return Array.isArray(value) ? value[0] : value;
    }

    // Mirror the time the user picks in the overlay so the calendar view can show
    // it before the vendor commits it to the model on submit.
    function recordPendingTime(hours: number, minutes: number) {
        pendingTime.value = { hours, minutes };
    }

    function selectTimeSlot(
        slot: TimeSlot,
        setHours: (hours: number) => void,
        setMinutes: (minutes: number) => void,
        setSeconds: (seconds: number) => void
    ) {
        setHours(slot.hours);
        setMinutes(slot.minutes);
        setSeconds(0);
        recordPendingTime(slot.hours, slot.minutes);
    }

    function wrapTimeValue(value: number, maximum: number): number {
        return (value + maximum) % maximum;
    }

    function adjustHours(
        hours: number | number[],
        minutes: number | number[],
        setHours: (hours: number) => void,
        direction: number
    ) {
        const nextHours = wrapTimeValue(firstTimeValue(hours) + direction, 24);
        setHours(nextHours);
        recordPendingTime(nextHours, firstTimeValue(minutes));
    }

    function adjustMinutes(
        hours: number | number[],
        minutes: number | number[],
        setMinutes: (minutes: number) => void,
        setSeconds: (seconds: number) => void,
        direction: number
    ) {
        const nextMinutes = wrapTimeValue(
            firstTimeValue(minutes) + direction * normalizedTimeSlotIntervalMinutes.value,
            60
        );
        setMinutes(nextMinutes);
        setSeconds(0);
        recordPendingTime(firstTimeValue(hours), nextMinutes);
    }

    function togglePeriod(
        hours: number | number[],
        minutes: number | number[],
        setHours: (hours: number) => void
    ) {
        const nextHours = wrapTimeValue(firstTimeValue(hours) + 12, 24);
        setHours(nextHours);
        recordPendingTime(nextHours, firstTimeValue(minutes));
    }
</script>

<style scoped>
    .activix-datepicker {
        --activix-datepicker-primary: #3e8dbc;
        --activix-datepicker-primary-hover: #2274a5;
        --activix-datepicker-range-background: #ebf8ff;
        --activix-datepicker-menu-padding-x: 0.875rem;
        --activix-datepicker-menu-padding-y: 0.75rem;
        --activix-datepicker-sidebar-width: 12.75rem;
        --activix-datepicker-sidebar-action-clearance: 3.75rem;
        --activix-datepicker-calendar-gap: 1rem;
        --activix-datepicker-surface: #ffffff;
        --activix-datepicker-border: #dfdfdf;
        --activix-datepicker-text: #232323;
        --activix-datepicker-text-muted: #3b3b3b;
        --activix-datepicker-scrollbar: #c7c7c7;
        --dp-primary-color: var(--activix-datepicker-primary);
        --dp-primary-text-color: var(--activix-datepicker-surface);
        --dp-hover-color: #f2f2f2;
        --dp-hover-text-color: var(--activix-datepicker-text);
        --dp-hover-icon-color: var(--activix-datepicker-text-muted);
        --dp-border-color: var(--activix-datepicker-border);
        --dp-menu-border-color: var(--activix-datepicker-border);
        --dp-range-between-dates-background-color: var(--activix-datepicker-range-background);
        --dp-range-between-dates-text-color: var(--activix-datepicker-text);
        --dp-range-between-border-color: var(--activix-datepicker-range-background);
        --dp-calendar-header-cell-padding: 0.25rem 0;
        --dp-cell-padding: 0.25rem;
        --dp-cell-border-radius: 0.375rem;
        --dp-cell-size: 2rem;
        --dp-border-radius: 0.25rem;
        --dp-font-size: 0.875rem;
        --dp-menu-padding: 0;
        --dp-month-year-row-button-size: 1.75rem;
        --dp-month-year-row-height: 2rem;
        --dp-multi-calendars-spacing: 0;
        --dp-row-margin: 0.125rem 0;
    }

    .activix-datepicker :deep(.dp--menu) {
        border: 1px solid var(--activix-datepicker-border);
        border-radius: 0.25rem;
        padding: var(--activix-datepicker-menu-padding-y) var(--activix-datepicker-menu-padding-x);
        /* shadow-origin-md */
        box-shadow: 0 0 6px -1px rgb(0 0 0 / 0.1), 0 0 4px -2px rgb(0 0 0 / 0.1);
    }

    .activix-datepicker :deep(.dp--menu:focus) {
        border: 1px solid var(--activix-datepicker-border);
        outline: none;
    }

    .activix-datepicker :deep(.dp--menu-inner) {
        padding: 0;
    }

    /* Use the vendor arrow (positioned by the floating engine so it tracks the
       trigger even when the menu shifts near a viewport edge); restyle it to match
       the menu surface and border. The nudge pulls it down onto the menu's border
       line, which our custom padding/border would otherwise leave a gap above. */
    .activix-datepicker :deep(.dp--arrow-top),
    .activix-datepicker :deep(.dp--arrow-bottom) {
        top: 0;
        background-color: var(--activix-datepicker-surface);
        border-color: var(--activix-datepicker-border);
    }

    .activix-datepicker :deep(.dp--menu-content-wrapper) {
        align-items: flex-start;
        gap: var(--activix-datepicker-calendar-gap);
    }

    .activix-datepicker :deep(.dp--menu-content-wrapper > .dp--instance-calendar) {
        border: 0;
        border-radius: 0;
        padding: 0;
        background: transparent;
    }

    .activix-datepicker--range :deep(.dp--menu-inner.dp--flex-display) {
        gap: var(--activix-datepicker-calendar-gap);
    }

    .activix-datepicker
        :deep(.dp--menu-content-wrapper > .dp--instance-calendar .dp--instance-calendar) {
        border: 1px solid var(--activix-datepicker-border);
        border-radius: 0.25rem;
        padding: 0.625rem 1rem 0.75rem;
        background: var(--activix-datepicker-surface);
    }

    .activix-datepicker :deep(.dp--instance-calendar + .dp--instance-calendar) {
        margin-left: 0;
    }

    .activix-datepicker :deep(.dp--month-year-row) {
        margin-bottom: 0.25rem;
    }

    .activix-datepicker :deep(.dp--month-year-select-base) {
        font-weight: 600;
    }

    .activix-datepicker :deep(.dp--calendar-header-cell) {
        border-bottom: 0;
        padding-bottom: 0.25rem;
    }

    .activix-datepicker :deep(.dp--calendar-header-item) {
        color: var(--activix-datepicker-text-muted);
        font-weight: 700;
    }

    .activix-datepicker :deep(.dp--cell-inner) {
        font-size: 0.875rem;
    }

    .activix-datepicker :deep(.dp--active),
    .activix-datepicker :deep(.dp--active:hover) {
        background: var(--activix-datepicker-primary);
        border-color: var(--activix-datepicker-primary);
        color: var(--activix-datepicker-surface);
    }

    .activix-datepicker :deep(.dp--today) {
        border-color: var(--activix-datepicker-primary);
    }

    .activix-datepicker :deep(.dp--sidebar-right) {
        border: 0;
        padding: 0;
    }

    .activix-datepicker :deep(.dp--action-row) {
        padding: 0;
    }

    .activix-datepicker:not(.activix-datepicker--datetime) :deep(.dp--tp-wrap) {
        display: none;
    }

    .activix-datepicker--datetime :deep([data-test-id='open-time-picker-btn']),
    .activix-datepicker--datetime :deep([data-test-id='close-time-picker-btn']),
    .activix-datepicker--datetime :deep(.dp--time-picker-overlay-container + .dp--button),
    .activix-datepicker--datetime :deep(.dp--time-picker-overlay-container ~ .dp--button),
    .activix-datepicker--datetime :deep(.activix-datepicker__time-overlay + .dp--button) {
        display: none;
    }

    .activix-datepicker :deep(.dp--time-picker-overlay-container) {
        padding: 0.5rem;
    }

    .activix-datepicker__preset-list {
        max-height: 18rem;
        width: var(--activix-datepicker-sidebar-width);
    }

    /* Keep the switcher, submit, and cancel buttons identical in size. */
    .activix-datepicker__action-button {
        height: 2.25rem;
        min-height: 2.25rem;
    }

    .activix-datepicker__submit-button {
        border: 1px solid var(--activix-datepicker-primary);
    }

    .activix-datepicker__submit-button:hover {
        border-color: var(--activix-datepicker-primary-hover);
    }

    .preset-scroll {
        scrollbar-width: thin;
        scrollbar-color: var(--activix-datepicker-scrollbar) transparent;
    }

    .preset-scroll::-webkit-scrollbar {
        width: 0.375rem;
    }

    .preset-scroll::-webkit-scrollbar-track {
        background: transparent;
    }

    .preset-scroll::-webkit-scrollbar-thumb {
        background: var(--activix-datepicker-scrollbar);
        border-radius: 9999px;
    }

    .preset-scroll::-webkit-scrollbar-thumb:hover {
        background: #9f9f9f;
    }

    @media (max-width: 639px) {
        .activix-datepicker--range.activix-datepicker--with-presets :deep(.dp--menu) {
            display: flex;
            width: min(calc(100vw - 1.5rem), 24rem);
            flex-direction: column;
            gap: 0.875rem;
            overflow: visible;
        }

        .activix-datepicker--range.activix-datepicker--with-presets :deep(.dp--menu-header),
        .activix-datepicker--range.activix-datepicker--with-presets
            :deep(.dp--menu-content-wrapper),
        .activix-datepicker--range.activix-datepicker--with-presets
            :deep(.dp--menu-content-wrapper > .dp--instance-calendar),
        .activix-datepicker--range.activix-datepicker--with-presets
            :deep(.dp--menu-content-wrapper > .dp--instance-calendar > .dp--menu-inner),
        .activix-datepicker--range.activix-datepicker--with-presets
            .activix-datepicker__range-header {
            display: contents;
        }

        .activix-datepicker--range.activix-datepicker--with-presets
            .activix-datepicker__range-label {
            width: 100%;
            padding: 0;
            text-align: center;
        }

        .activix-datepicker--range.activix-datepicker--with-presets
            .activix-datepicker__range-label--start {
            order: 2;
        }

        .activix-datepicker--range.activix-datepicker--with-presets
            .activix-datepicker__range-label--end {
            order: 4;
        }

        .activix-datepicker--range.activix-datepicker--with-presets :deep(.dp--sidebar-right) {
            order: 1;
            width: 100%;
        }

        .activix-datepicker--range.activix-datepicker--with-presets
            .activix-datepicker__preset-list {
            width: 100%;
            max-height: 16rem;
        }

        .activix-datepicker--range.activix-datepicker--with-presets
            :deep(
                .dp--menu-content-wrapper
                    > .dp--instance-calendar
                    > .dp--menu-inner
                    > .dp--instance-calendar:first-child
            ) {
            order: 3;
            width: 100%;
        }

        .activix-datepicker--range.activix-datepicker--with-presets
            :deep(
                .dp--menu-content-wrapper
                    > .dp--instance-calendar
                    > .dp--menu-inner
                    > .dp--instance-calendar:nth-child(2)
            ) {
            order: 5;
            width: 100%;
        }

        .activix-datepicker--range.activix-datepicker--with-presets :deep(.dp--action-row) {
            order: 6;
            width: 100%;
        }
    }

    @media (min-width: 640px) {
        .activix-datepicker--range.activix-datepicker--with-presets :deep(.dp--menu) {
            position: relative;
            padding-right: calc(
                var(--activix-datepicker-menu-padding-x) + var(--activix-datepicker-sidebar-width) +
                    var(--activix-datepicker-calendar-gap)
            );
        }

        .activix-datepicker--range.activix-datepicker--with-presets :deep(.dp--menu-header) {
            width: auto;
        }

        .activix-datepicker--range.activix-datepicker--with-presets
            :deep(.dp--menu-content-wrapper > .dp--sidebar-right) {
            position: absolute;
            top: var(--activix-datepicker-menu-padding-y);
            right: var(--activix-datepicker-menu-padding-x);
            bottom: var(--activix-datepicker-menu-padding-y);
            display: flex;
            width: var(--activix-datepicker-sidebar-width);
            min-height: 0;
            overflow: hidden;
        }

        .activix-datepicker--range.activix-datepicker--with-presets :deep(.dp--action-row) {
            position: absolute;
            right: var(--activix-datepicker-menu-padding-x);
            bottom: var(--activix-datepicker-menu-padding-y);
            z-index: 1;
            /*
             * Right-anchored and sized to its content rather than pinned to the
             * sidebar width: the clear button makes three controls, which no
             * longer fit the narrow sidebar column, so let the row grow leftward
             * into the empty space under the calendars instead of overflowing.
             */
            width: max-content;
            min-width: var(--activix-datepicker-sidebar-width);
        }

        .activix-datepicker--range.activix-datepicker--with-presets
            .activix-datepicker__preset-list {
            height: calc(100% - var(--activix-datepicker-sidebar-action-clearance));
            max-height: none;
            min-height: 0;
        }

        .activix-datepicker--range.activix-datepicker--with-presets .preset-scroll {
            min-height: 0;
        }
    }
</style>
